import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  FolderOpen,
  Inbox,
  LayoutDashboard,
  LogOut,
  Plus,
  Search,
  Settings,
  Shield,
  Upload,
  Users,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ baseURL: API, withCredentials: true });

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function Sidebar({ activeTab, setActiveTab, user, onLogout }) {
  const isAdmin = user?.role === 'admin';
  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'matters', label: 'Matters', icon: Inbox },
    { id: 'uploads', label: 'Uploads', icon: FolderOpen },
    ...(isAdmin ? [
      { id: 'users', label: 'Users', icon: Users },
      { id: 'settings', label: 'Settings', icon: Settings },
    ] : []),
  ];

  return (
    <aside className="w-64 bg-white border-r border-[#D4D4D0] flex flex-col h-screen fixed left-0 top-0 z-40">
      <div className="p-5 border-b border-[#D4D4D0]">
        <Link to="/">
          <img src="/10_VARfacts_logo_header_400x100.png" alt="VARfacts" className="h-7" />
        </Link>
      </div>
      <nav className="flex-1 p-4 space-y-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`sidebar-link w-full text-left ${activeTab === tab.id ? 'active' : ''}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </nav>
      <div className="p-4 border-t border-[#D4D4D0] space-y-3">
        <div className="flex items-center gap-3">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-[#0B1F3F] flex items-center justify-center text-white text-xs font-bold">
              {user?.name?.[0] || '?'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#0B1F3F] truncate">{user?.name}</p>
            <p className="text-xs text-[#94A3B8] truncate">{user?.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs capitalize">{user?.role}</Badge>
          <button onClick={onLogout} className="ml-auto text-[#94A3B8] hover:text-red-500 transition-colors">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}

function OverviewTab({ stats, matters, batches, user }) {
  const isAdmin = user?.role === 'admin';
  const recentMatters = matters.slice(0, 5);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-heading text-2xl text-[#0B1F3F] font-semibold">
          Welcome, {user?.name?.split(' ')[0]}
        </h2>
        <p className="text-sm text-[#64748B] mt-1">
          {isAdmin ? 'Account, matter, and upload activity overview' : 'Your account overview'}
        </p>
      </div>

      {isAdmin && stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Total Users', value: stats.total_users, icon: Users },
            { label: 'Total Matters', value: stats.total_matters, icon: Inbox },
            { label: 'Upload Batches', value: stats.total_upload_batches, icon: FolderOpen },
          ].map((item) => (
            <div key={item.label} className="bg-white border border-[#D4D4D0] rounded-sm p-5 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">{item.label}</p>
                <item.icon className="w-4 h-4 text-[#C5A55A]" />
              </div>
              <p className="text-2xl font-bold text-[#0B1F3F]">{item.value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#D4D4D0] rounded-sm overflow-hidden">
          <div className="p-5 flex items-center justify-between border-b border-[#D4D4D0]">
            <h3 className="font-heading text-lg text-[#0B1F3F] font-semibold">Recent Matters</h3>
            <Link to="/intake" className="text-xs text-[#C5A55A] font-semibold uppercase tracking-wide hover:underline flex items-center gap-1">
              New Matter <Plus className="w-3 h-3" />
            </Link>
          </div>
          {recentMatters.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#94A3B8]">No matters yet</div>
          ) : (
            <table className="w-full data-table">
              <thead>
                <tr className="text-left">
                  <th>Matter</th>
                  <th>Reference</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {recentMatters.map((matter) => (
                  <tr key={matter.matter_id}>
                    <td className="text-[#0B1F3F] font-medium">{matter.matter_name}</td>
                    <td className="text-[#64748B] text-xs">{matter.matter_reference}</td>
                    <td className="text-[#94A3B8] text-xs">{formatDate(matter.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="bg-white border border-[#D4D4D0] rounded-sm overflow-hidden">
          <div className="p-5 flex items-center justify-between border-b border-[#D4D4D0]">
            <h3 className="font-heading text-lg text-[#0B1F3F] font-semibold">Recent Upload Batches</h3>
            <Link to="/upload-records" className="text-xs text-[#C5A55A] font-semibold uppercase tracking-wide hover:underline flex items-center gap-1">
              Existing Matter Upload <Upload className="w-3 h-3" />
            </Link>
          </div>
          {batches.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#94A3B8]">No upload batches yet</div>
          ) : (
            <table className="w-full data-table">
              <thead>
                <tr className="text-left">
                  <th>Batch</th>
                  <th>Matter</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {batches.slice(0, 5).map((batch) => (
                  <tr key={batch.batch_id}>
                    <td className="text-[#0B1F3F] font-medium">{batch.batch_id}</td>
                    <td className="text-[#64748B] text-xs">{batch.matter_name}</td>
                    <td className="text-[#94A3B8] text-xs">{formatDate(batch.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function MattersTab({ matters, user }) {
  const [search, setSearch] = useState('');
  const filtered = matters.filter((matter) =>
    matter.matter_name?.toLowerCase().includes(search.toLowerCase()) ||
    matter.matter_reference?.toLowerCase().includes(search.toLowerCase()) ||
    matter.client_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl text-[#0B1F3F] font-semibold">Matters</h2>
          <p className="text-sm text-[#64748B] mt-1">
            {user?.role === 'admin' ? 'All account matters' : 'Matters in your account'}
          </p>
        </div>
        <Button asChild className="btn-gold rounded-sm px-4 py-2 h-10">
          <Link to="/intake"><Plus className="w-4 h-4 mr-2" /> Start Intake</Link>
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
        <Input
          placeholder="Search matters..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-10 bg-white border-[#D4D4D0]"
        />
      </div>

      <div className="bg-white border border-[#D4D4D0] rounded-sm overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-[#94A3B8]">No matters found</div>
        ) : (
          <table className="w-full data-table">
            <thead>
              <tr className="text-left">
                <th>Matter</th>
                <th>Reference</th>
                <th>Client</th>
                <th>Case Type</th>
                <th>Date</th>
                <th>Upload</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((matter) => (
                <tr key={matter.matter_id}>
                  <td className="text-[#0B1F3F] font-medium">{matter.matter_name}</td>
                  <td className="text-[#64748B] text-xs">{matter.matter_reference}</td>
                  <td className="text-[#64748B] text-xs">{matter.client_name}</td>
                  <td className="text-[#64748B] text-xs capitalize">{matter.case_type || '—'}</td>
                  <td className="text-[#94A3B8] text-xs">{formatDate(matter.created_at)}</td>
                  <td>
                    <Link
                      to={`/matters/${matter.matter_id}/upload`}
                      className="text-[#C5A55A] hover:text-[#0B1F3F] text-xs font-semibold uppercase tracking-wide"
                    >
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function UploadsTab({ matters, batches, user }) {
  const matterMap = Object.fromEntries(matters.map((matter) => [matter.matter_id, matter]));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl text-[#0B1F3F] font-semibold">Upload Batches</h2>
          <p className="text-sm text-[#64748B] mt-1">
            {user?.role === 'admin' ? 'All Sync upload batches' : 'Your matter-specific Sync upload batches'}
          </p>
        </div>
        <Button asChild className="btn-gold rounded-sm px-4 py-2 h-10">
          <Link to="/upload-records"><Upload className="w-4 h-4 mr-2" /> Upload Additional Records</Link>
        </Button>
      </div>

      <div className="bg-white border border-[#D4D4D0] rounded-sm overflow-hidden">
        {batches.length === 0 ? (
          <div className="p-12 text-center space-y-3">
            <FolderOpen className="w-12 h-12 text-[#D4D4D0] mx-auto" />
            <p className="text-sm text-[#94A3B8]">No upload batches recorded yet</p>
          </div>
        ) : (
          <table className="w-full data-table">
            <thead>
              <tr className="text-left">
                <th>Batch</th>
                <th>Matter</th>
                <th>Reference</th>
                <th>Method</th>
                <th>Date</th>
                <th>Open Matter</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((batch) => {
                const matter = matterMap[batch.matter_id];
                return (
                  <tr key={batch.batch_id}>
                    <td className="text-[#0B1F3F] font-medium">{batch.batch_id}</td>
                    <td className="text-[#64748B] text-xs">{matter?.matter_name || batch.matter_id}</td>
                    <td className="text-[#64748B] text-xs">{matter?.matter_reference || '—'}</td>
                    <td className="text-[#64748B] text-xs uppercase">{batch.transfer_method}</td>
                    <td className="text-[#94A3B8] text-xs">{formatDate(batch.created_at)}</td>
                    <td>
                      <Link
                        to={`/matters/${batch.matter_id}/upload`}
                        className="text-[#C5A55A] hover:text-[#0B1F3F] text-xs font-semibold uppercase tracking-wide"
                      >
                        Open
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function UsersTab({ allUsers, refreshUsers, currentUser }) {
  const [editUser, setEditUser] = useState(null);
  const [editRole, setEditRole] = useState('');
  const [search, setSearch] = useState('');

  const filtered = allUsers.filter((user) =>
    user.name?.toLowerCase().includes(search.toLowerCase()) ||
    user.email?.toLowerCase().includes(search.toLowerCase())
  );

  const handleSave = async () => {
    try {
      await ax.patch(`/admin/users/${editUser.user_id}`, { role: editRole });
      toast.success('User updated');
      setEditUser(null);
      refreshUsers();
    } catch {
      toast.error('Failed to update user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Delete this user and all their data?')) return;
    try {
      await ax.delete(`/admin/users/${userId}`);
      toast.success('User deleted');
      refreshUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl text-[#0B1F3F] font-semibold">User Management</h2>
        <p className="text-sm text-[#64748B] mt-1">Manage user accounts and roles</p>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
        <Input
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-10 bg-white border-[#D4D4D0]"
        />
      </div>

      <div className="bg-white border border-[#D4D4D0] rounded-sm overflow-hidden">
        <table className="w-full data-table">
          <thead>
            <tr className="text-left">
              <th>User</th>
              <th>Email</th>
              <th>Role</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((user) => (
              <tr key={user.user_id}>
                <td>
                  <div className="flex items-center gap-3">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-[#0B1F3F] flex items-center justify-center text-white text-xs font-bold">
                        {user.name?.[0] || '?'}
                      </div>
                    )}
                    <span className="text-[#0B1F3F] font-medium">{user.name}</span>
                  </div>
                </td>
                <td className="text-[#64748B] text-xs">{user.email}</td>
                <td>
                  <Badge variant="outline" className={`text-xs capitalize ${user.role === 'admin' ? 'border-[#C5A55A] text-[#C5A55A]' : ''}`}>
                    {user.role === 'admin' && <Shield className="w-3 h-3 mr-1" />}
                    {user.role}
                  </Badge>
                </td>
                <td className="text-[#94A3B8] text-xs">{formatDate(user.created_at)}</td>
                <td>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setEditUser(user);
                        setEditRole(user.role);
                      }}
                      className="text-[#C5A55A] hover:text-[#0B1F3F] text-xs font-semibold uppercase tracking-wide"
                    >
                      Edit
                    </button>
                    {user.user_id !== currentUser?.user_id && (
                      <button onClick={() => handleDelete(user.user_id)} className="text-[#94A3B8] hover:text-red-500">
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#0B1F3F]">Edit User</DialogTitle>
          </DialogHeader>
          {editUser && (
            <div className="space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Name</p>
                <p className="text-[#0B1F3F] font-medium">{editUser.name}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Email</p>
                <p className="text-sm text-[#64748B]">{editUser.email}</p>
              </div>
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Role</p>
                <Select value={editRole} onValueChange={setEditRole}>
                  <SelectTrigger className="h-10 bg-white border-[#D4D4D0]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} className="btn-secondary-varfacts rounded-sm">Cancel</Button>
            <Button onClick={handleSave} className="btn-primary-varfacts rounded-sm">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SettingsTab() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl text-[#0B1F3F] font-semibold">Settings</h2>
        <p className="text-sm text-[#64748B] mt-1">Current transfer and access posture</p>
      </div>
      <div className="bg-white border border-[#D4D4D0] rounded-sm p-6 space-y-4">
        <h3 className="font-heading text-lg text-[#0B1F3F] font-semibold">Transfer Method</h3>
        <Separator className="bg-[#D4D4D0]" />
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Method</p>
            <p className="text-[#0B1F3F] mt-1">Sync</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Routing</p>
            <p className="text-[#0B1F3F] mt-1">Matter-specific upload path</p>
          </div>
        </div>
      </div>
      <div className="bg-white border border-[#D4D4D0] rounded-sm p-6 space-y-4">
        <h3 className="font-heading text-lg text-[#0B1F3F] font-semibold">Security Posture</h3>
        <Separator className="bg-[#D4D4D0]" />
        <div className="text-sm text-[#64748B] space-y-2">
          <p>Account access is required before a matter is created or an existing matter upload path is opened.</p>
          <p>All sessions expire after 7 days. Cookie-based auth with httpOnly, secure, SameSite=None.</p>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { tab: urlTab } = useParams();
  const [activeTab, setActiveTab] = useState(urlTab || 'overview');
  const [matters, setMatters] = useState([]);
  const [batches, setBatches] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [stats, setStats] = useState(null);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    setActiveTab(urlTab || 'overview');
  }, [urlTab]);

  useEffect(() => {
    const load = async () => {
      try {
        const [matterRes, batchRes] = await Promise.all([
          ax.get('/matters'),
          ax.get('/upload-batches'),
        ]);
        setMatters(matterRes.data);
        setBatches(batchRes.data);
      } catch {
        toast.error('Failed to load account data');
      }

      if (isAdmin) {
        try {
          const [usersRes, statsRes] = await Promise.all([
            ax.get('/admin/users'),
            ax.get('/admin/stats'),
          ]);
          setAllUsers(usersRes.data);
          setStats(statsRes.data);
        } catch {
          toast.error('Failed to load admin data');
        }
      }
    };

    load();
  }, [isAdmin]);

  const refreshUsers = async () => {
    if (!isAdmin) return;
    const res = await ax.get('/admin/users');
    setAllUsers(res.data);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/dashboard/${tab}`, { replace: true });
  };

  const batchesWithMatterNames = batches.map((batch) => ({
    ...batch,
    matter_name: matters.find((matter) => matter.matter_id === batch.matter_id)?.matter_name || batch.matter_id,
  }));

  return (
    <div className="min-h-screen bg-[#F5F3EF]">
      <Sidebar activeTab={activeTab} setActiveTab={handleTabChange} user={user} onLogout={handleLogout} />
      <main className="ml-64 p-8">
        {activeTab === 'overview' && <OverviewTab stats={stats} matters={matters} batches={batchesWithMatterNames} user={user} />}
        {activeTab === 'matters' && <MattersTab matters={matters} user={user} />}
        {activeTab === 'uploads' && <UploadsTab matters={matters} batches={batches} user={user} />}
        {activeTab === 'users' && isAdmin && <UsersTab allUsers={allUsers} refreshUsers={refreshUsers} currentUser={user} />}
        {activeTab === 'settings' && isAdmin && <SettingsTab />}
      </main>
    </div>
  );
}
